<template>
  <div class="form-group">
    <label v-tooltip.top="labelTooltip || undefined" :for="name">{{
      t(`labels.${props.label ?? props.name}`)
    }}</label>
    <InputNumber
      :id="name"
      v-model="internalValue"
      class="w-full"
      :inputId="inputId"
      :minFractionDigits="minFractionDigits"
      :maxFractionDigits="maxFractionDigits"
      :mode="formatMode"
      :currency="currency"
      :prefix="prefix"
      :suffix="suffix"
      :locale="currentLocale"
      :useGrouping="useGrouping"
      :disabled="disabled"
      :class="{
        'dirty-state-wrapper': isFieldDirty,
        'error-state-wrapper': showError,
      }"
      :min="minValue"
      :max="maxValue"
      @input="handleInput"
    />
    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import type { InputNumberInputEvent } from 'primevue/inputnumber';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import FormError from './errors/FormError.vue';

const props = defineProps<{
  name: string;
  modelValue: number | undefined;
  initialValue: number | undefined;
  isDirty: (dirty: boolean) => void;
  label?: string;
  labelTooltip?: string;
  disabled?: boolean;
  inputId?: string;
  useGrouping?: boolean; // Optional prop to enable/disable grouping separators
  formatType?: 'decimal' | 'currency'; // Optional format type (decimal or currency)
  currency?: string; // Optional currency symbol (e.g., EUR, USD)
  minValue?: number;
  maxValue?: number;
  prefix?: string;
  suffix?: string;
  minFractionDigits?: number;
  maxFractionDigits?: number;
}>();

const { t, locale } = useI18n();

const { errorMessage, meta } = useField<number>(props.name);
const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const internalValue = ref(props.modelValue);
const initialValue = computed(() => props.initialValue);
const isFieldDirty = ref(false); // Track dirty state
const inputId = computed(() => props.inputId || 'locale-user');

// Set up format options based on props and locale
const formatMode = computed(() => props.formatType || 'decimal');
const currency = computed(() => {
  if (formatMode.value === 'currency' && !props.currency) {
    return 'EUR';
  }
  return props.currency;
});

// Use the current locale from i18n for number formatting
const currentLocale = computed(() => {
  switch (locale.value) {
    case 'de':
      return 'de-DE';
    case 'fr':
      return 'fr-FR';
    case 'hu':
      return 'hu-HU';
    case 'en':
    default:
      return 'en-GB'; // Default to UK English
  }
});

const minFractionDigits = computed(() =>
  props.minFractionDigits !== undefined
    ? props.minFractionDigits
    : formatMode.value === 'currency'
      ? 2
      : 0,
);
const maxFractionDigits = computed(() =>
  props.maxFractionDigits !== undefined
    ? props.maxFractionDigits
    : formatMode.value === 'currency'
      ? 2
      : 2,
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null | undefined): void;
}>();

const handleInput = (event: InputNumberInputEvent) => {
  const value = typeof event.value === 'number' || event.value === undefined ? event.value : null;
  emit('update:modelValue', value);
};

// Watch for changes in internalValue and initialValue to track dirty state
watch([internalValue, initialValue], () => {
  isFieldDirty.value = internalValue.value !== initialValue.value;
  props.isDirty(isFieldDirty.value);
});

// Watch for changes in props.modelValue and update internalValue accordingly
watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue;
  },
);
</script>
